/* eslint-disable camelcase */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import GeoLocationComponent, {
  GeoLocation,
} from 'components/GeoLocationComponent/GeoLocationComponent';
import { RESOURCE_SET_STATUSES } from 'components/IAP/ICSConstants';
import { useGetRosterForIncident } from 'hooks/incident/useRoster';
import { useUpdateResourceRelationships } from 'hooks/resources/useResourceRelationships';
import React, { useState } from 'react';
import { Form, Offcanvas, Row } from 'react-bootstrap';
import { FormProvider, useForm } from 'react-hook-form';
import { selectGroupGuid, useAppSelector } from '../../slices/commonSelectors';
import { StylishNewButton } from '../DesignSystems/New/StylishNewButton';
import CustomResourceAddDrawer from './CustomResourceTypeAddDrawer';
import DrawerWrapper, { DrawerFooter } from './DrawerWrapper';
import { AddEntityDrawerProps } from './EntityListProps';
import FormDateTimeField from './formFields/FormDateTimeField';
import FormSelectField from './formFields/FormSelectField';
import FormTextAreaField from './formFields/FormTextAreaField';
import FormTextField from './formFields/FormTextField';
import RelationshipPickerField from './formFields/RelationshipPickerField';
import { useCustomResourcePersistence } from './hooks/customResourceTypeHooks';
import {
  CreateResourceSetFormData,
  useCreateResourceSet,
} from './hooks/resourceSetHooks';
import { useResourceSites } from './hooks/resourceSiteHooks';
import { useScopedIncident } from './hooks/incidentHooks';
import ResourceSelectFields from './ResourceSelectFields';

const ResourceSetAddDrawer: React.FC<AddEntityDrawerProps> = ({ toggle }) => {
  // Set up form
  const form = useForm<CreateResourceSetFormData>({
    mode: 'onChange',
  });
  const { setValue, watch } = form;

  const watchedName = watch('name');
  const watchedResource = watch('kind');
  const watchedType = watch('type');
  const watchedQuantity = watch('quantity');
  const watchedSetStatus = watch('status');

  const { data: resourceSites } = useResourceSites();

  const groupGuid = useAppSelector(selectGroupGuid);
  const mutation = useCreateResourceSet();
  const [geolocation, setGeolocation] = useState<GeoLocation>();
  const [sourceLocation, setSourceLocation] = useState<GeoLocation>();
  const [destinationLocation, setDestinationLocation] = useState<GeoLocation>();
  const [showCustomResourceDrawer, setShowCustomResourceDrawer] =
    useState(false);

  // Location type toggles
  const [useCustomCurrentLocation, setUseCustomCurrentLocation] =
    useState(false);
  const [useCustomSourceLocation, setUseCustomSourceLocation] = useState(false);
  const [useCustomDestinationLocation, setUseCustomDestinationLocation] =
    useState(false);
  const { persistPendingResources } = useCustomResourcePersistence();

  // Fetch incidents and current incident with proper types
  const currentIncident = useScopedIncident();

  // Fetch incident members
  const { data: rosterData = [] } = useGetRosterForIncident(
    currentIncident?.id
  );
  const updateRelationships = useUpdateResourceRelationships();

  // Remove default incident assignment
  React.useEffect(() => {
    setValue('incidents', []);
  }, [setValue]);

  const onSubmit = async (formData: CreateResourceSetFormData) => {
    await persistPendingResources(form.watch('kind'), form.watch('type'));
    await onNewResourceSetDataSubmit(formData);
  };

  const onNewResourceSetDataSubmit = async (
    formData: CreateResourceSetFormData
  ) => {
    const payload = {
      ...formData,
      // Current location - either custom or site, not both
      geolocation: useCustomCurrentLocation ? geolocation : null,
      resourceSiteId: useCustomCurrentLocation ? null : formData.resourceSiteId,
      // Source location - either custom or site, not both
      sourceLocation: useCustomSourceLocation ? sourceLocation : null,
      sourceResourceSiteId: useCustomSourceLocation
        ? null
        : formData.sourceResourceSiteId,
      // Destination location - either custom or site, not both
      destinationLocation: useCustomDestinationLocation
        ? destinationLocation
        : null,
      destinationResourceSiteId: useCustomDestinationLocation
        ? null
        : formData.destinationResourceSiteId,
      archived: false,
      incidents: [], // Always empty on creation
      groupGuid: groupGuid,
    };

    // Create the resource first
    const createdResource = await mutation.mutateAsync(payload);

    // Then assign members if any were selected
    if (
      formData.members &&
      formData.members.length > 0 &&
      createdResource?.data?.resource_set_id
    ) {
      await updateRelationships.mutateAsync({
        entityId: createdResource.data.resource_set_id,
        entityType: 'resource',
        relatedIds: formData.members,
      });
    }

    toggle();
  };

  return (
    <DrawerWrapper toggle={toggle} title="Add Resource">
      <FormProvider {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="d-flex flex-column justify-content-between flex-grow-1"
        >
          <Offcanvas.Body>
            <Row>
              <FormTextField
                title="Name"
                fieldName="name"
                rules={{
                  required: { value: true, message: 'This field is required' },
                  maxLength: {
                    value: 300,
                    message: 'This maximum length is 300 characters',
                  },
                }}
                variant="half"
              />
            </Row>
            <Row>
              <FormTextAreaField
                title="Description"
                fieldName="description"
                variant="full"
              />
            </Row>
            <ResourceSelectFields labels />
            <Row>
              <FormTextField
                title="Quantity"
                fieldName="quantity"
                rules={{
                  required: { value: true, message: 'This field is required' },
                  maxLength: {
                    value: 300,
                    message: 'This maximum length is 300 characters',
                  },
                }}
                variant="half"
              />
            </Row>
            <Row>
              <FormDateTimeField
                title="Date Time Ordered"
                fieldName="date_time_ordered"
                variant="half"
                type="datetime-local"
              />
            </Row>
            <Row>
              <FormDateTimeField
                title="ETA"
                fieldName="eta"
                variant="half"
                type="datetime-local"
              />
            </Row>
            <Row>
              <FormSelectField
                title="Set Status"
                fieldName="status"
                rules={{
                  required: { value: true, message: 'This field is required' },
                }}
                options={
                  RESOURCE_SET_STATUSES.map((u) => ({
                    label: u.label,
                    value: u.value,
                  })) || []
                }
                variant="half"
              />
            </Row>
            {/* Location fields - conditional based on status */}
            {(watchedSetStatus === 'In Transit' ||
              watchedSetStatus === 'Ordered') && (
              <>
                <Row>
                  <div className="mb-3">
                    <h6>Location Information</h6>
                  </div>
                </Row>

                {/* Source Location */}
                <Row className="mb-3">
                  <div className="col-12">
                    <Form.Check
                      type="switch"
                      id="use-custom-source-location"
                      label="Use Custom Source Location"
                      checked={useCustomSourceLocation}
                      onChange={(e) => {
                        setUseCustomSourceLocation(e.target.checked);
                        if (e.target.checked) {
                          setValue('sourceResourceSiteId', '');
                        } else {
                          setSourceLocation(undefined);
                        }
                      }}
                      className="mb-2"
                    />
                    {useCustomSourceLocation ? (
                      <div>
                        <label className="form-label">Source Location:</label>
                        <GeoLocationComponent
                          location={sourceLocation}
                          setLocation={setSourceLocation}
                        />
                      </div>
                    ) : (
                      <FormSelectField
                        title="Source Site"
                        fieldName="sourceResourceSiteId"
                        options={
                          resourceSites
                            ?.filter((r) => !r.archived)
                            ?.map((r) => ({ label: r.name, value: r.id })) || []
                        }
                        variant="full"
                      />
                    )}
                  </div>
                </Row>

                {/* Current Location */}
                <Row className="mb-3">
                  <div className="col-12">
                    <Form.Check
                      type="switch"
                      id="use-custom-current-location"
                      label="Use Custom Current Location"
                      checked={useCustomCurrentLocation}
                      onChange={(e) => {
                        setUseCustomCurrentLocation(e.target.checked);
                        if (e.target.checked) {
                          setValue('resourceSiteId', '');
                        } else {
                          setGeolocation(undefined);
                        }
                      }}
                      className="mb-2"
                    />
                    {useCustomCurrentLocation ? (
                      <div>
                        <label className="form-label">Current Location:</label>
                        <GeoLocationComponent
                          location={geolocation}
                          setLocation={setGeolocation}
                        />
                      </div>
                    ) : (
                      <FormSelectField
                        title="Current Site"
                        fieldName="resourceSiteId"
                        options={
                          resourceSites
                            ?.filter((r) => !r.archived)
                            ?.map((r) => ({ label: r.name, value: r.id })) || []
                        }
                        variant="full"
                      />
                    )}
                  </div>
                </Row>

                {/* Destination Location */}
                <Row className="mb-3">
                  <div className="col-12">
                    <Form.Check
                      type="switch"
                      id="use-custom-destination-location"
                      label="Use Custom Destination Location"
                      checked={useCustomDestinationLocation}
                      onChange={(e) => {
                        setUseCustomDestinationLocation(e.target.checked);
                        if (e.target.checked) {
                          setValue('destinationResourceSiteId', '');
                        } else {
                          setDestinationLocation(undefined);
                        }
                      }}
                      className="mb-2"
                    />
                    {useCustomDestinationLocation ? (
                      <div>
                        <label className="form-label">
                          Destination Location:
                        </label>
                        <GeoLocationComponent
                          location={destinationLocation}
                          setLocation={setDestinationLocation}
                        />
                      </div>
                    ) : (
                      <FormSelectField
                        title="Destination Site"
                        fieldName="destinationResourceSiteId"
                        options={
                          resourceSites
                            ?.filter((r) => !r.archived)
                            ?.map((r) => ({ label: r.name, value: r.id })) || []
                        }
                        variant="full"
                      />
                    )}
                  </div>
                </Row>
              </>
            )}

            {/* Non-transit resources - only current location */}
            {watchedSetStatus &&
              watchedSetStatus !== 'In Transit' &&
              watchedSetStatus !== 'Ordered' && (
                <Row className="mb-3">
                  <div className="col-12">
                    <Form.Check
                      type="switch"
                      id="use-custom-current-location"
                      label="Use Custom Location"
                      checked={useCustomCurrentLocation}
                      onChange={(e) => {
                        setUseCustomCurrentLocation(e.target.checked);
                        if (e.target.checked) {
                          setValue('resourceSiteId', '');
                        } else {
                          setGeolocation(undefined);
                        }
                      }}
                      className="mb-2"
                    />
                    {useCustomCurrentLocation ? (
                      <div>
                        <label className="form-label">Current Location:</label>
                        <GeoLocationComponent
                          location={geolocation}
                          setLocation={setGeolocation}
                        />
                      </div>
                    ) : (
                      <FormSelectField
                        title="Resource Site"
                        fieldName="resourceSiteId"
                        options={
                          resourceSites
                            ?.filter((r) => !r.archived)
                            ?.map((r) => ({ label: r.name, value: r.id })) || []
                        }
                        variant="full"
                      />
                    )}
                  </div>
                </Row>
              )}
            <Row>
              <div className="mb-3">
                <label className="form-label">Incidents</label>
                <div className="text-muted">
                  <small>
                    Resources will be automatically added to incidents when
                    assigned
                  </small>
                </div>
              </div>
            </Row>
            <Row>
              <div className="text-muted">
                <small>
                  The available members are those that are found in the roster
                  of the currently selected incident. The currently selected
                  incident is {currentIncident?.name}
                </small>
              </div>
              <RelationshipPickerField
                label="Assign Members"
                fieldName="members"
                defaultValue={[]}
                options={
                  rosterData?.map((member) => ({
                    ...member,
                    id: member.incidentMemberId,
                  })) || []
                }
                identity={(member) => member.id}
                title={(member) => member.emailAddress || member.userName || ''}
                cardType="incident-member"
              />
            </Row>
          </Offcanvas.Body>
          <DrawerFooter>
            <div className="button-group">
              <StylishNewButton
                className="button--secondary button--reverse"
                type="button"
                onClick={toggle}
              >
                Close
              </StylishNewButton>
              <StylishNewButton
                className="button--primary"
                type="submit"
                disabled={
                  !watchedName ||
                  !watchedResource ||
                  !watchedQuantity ||
                  !watchedSetStatus ||
                  !watchedType ||
                  mutation.isPending
                }
              >
                Save
              </StylishNewButton>
            </div>
          </DrawerFooter>
        </form>
      </FormProvider>
      {showCustomResourceDrawer && (
        <CustomResourceAddDrawer
          toggle={() => setShowCustomResourceDrawer(false)}
        />
      )}
    </DrawerWrapper>
  );
};

export default ResourceSetAddDrawer;
